import React, { useEffect, useState } from "react";

import {
  FaPlus,
  FaTrash,
  FaSave,
  FaEdit,
  FaTimes,
  FaChevronRight,
  FaArrowLeft,
} from "react-icons/fa";

import "./Rules.css";
import { API } from "./../../config/api"; // ✅ Import API

const API_URL = `${API.BASE_URL}/rules.php`;

export default function Rules() {
  const [rulesData, setRulesData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  /*
  |--------------------------------------------------------------------------
  | FETCH RULES
  |--------------------------------------------------------------------------
  */

  const fetchRules = async () => {
    try {
      setLoading(true);

      const response = await fetch(API_URL);

      const result = await response.json();

      if (result.success) {
        setRulesData(result.data || {});
      } else {
        alert(result.message || "Failed to fetch rules.");
      }
    } catch (error) {
      console.error("Rules fetch error:", error);
      alert("Unable to fetch rules.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  /*
  |--------------------------------------------------------------------------
  | SAVE RULES
  |--------------------------------------------------------------------------
  */

  const saveRules = async () => {
    try {
      setSaving(true);

      const response = await fetch(API_URL, {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify(rulesData),
      });

      const result = await response.json();

      if (result.success) {
        setRulesData(result.data || rulesData);
        setEditing(false);

        alert("Rules updated successfully.");
      } else {
        alert(result.message || "Unable to update rules.");
      }
    } catch (error) {
      console.error("Save error:", error);
      alert("Unable to save rules.");
    } finally {
      setSaving(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | CHANGE CATEGORY TITLE
  |--------------------------------------------------------------------------
  */

  const changeTitle = (category, value) => {
    setRulesData((prev) => ({
      ...prev,

      [category]: {
        ...prev[category],
        title: value,
      },
    }));
  };

  /*
  |--------------------------------------------------------------------------
  | CHANGE SIMPLE RULE
  |--------------------------------------------------------------------------
  */

  const changeSimpleRule = (category, ruleIndex, value) => {
    setRulesData((prev) => {
      const updatedRules = [...(prev[category]?.rules || [])];

      updatedRules[ruleIndex] = value;

      return {
        ...prev,

        [category]: {
          ...prev[category],
          rules: updatedRules,
        },
      };
    });
  };

  /*
  |--------------------------------------------------------------------------
  | ADD SIMPLE RULE
  |--------------------------------------------------------------------------
  */

  const addSimpleRule = (category) => {
    setRulesData((prev) => ({
      ...prev,

      [category]: {
        ...prev[category],

        rules: [...(prev[category]?.rules || []), ""],
      },
    }));
  };

  /*
  |--------------------------------------------------------------------------
  | DELETE SIMPLE RULE
  |--------------------------------------------------------------------------
  */

  const deleteSimpleRule = (category, ruleIndex) => {
    if (!window.confirm("Are you sure you want to delete this rule?")) {
      return;
    }

    setRulesData((prev) => ({
      ...prev,

      [category]: {
        ...prev[category],

        rules: (prev[category]?.rules || []).filter(
          (_, index) => index !== ruleIndex,
        ),
      },
    }));
  };

  /*
  |--------------------------------------------------------------------------
  | CHANGE SECTION HEADING
  |--------------------------------------------------------------------------
  */

  const changeSectionHeading = (category, sectionIndex, value) => {
    setRulesData((prev) => {
      const sections = [...(prev[category]?.sections || [])];

      sections[sectionIndex] = {
        ...sections[sectionIndex],
        heading: value,
      };

      return {
        ...prev,

        [category]: {
          ...prev[category],
          sections,
        },
      };
    });
  };

  /*
  |--------------------------------------------------------------------------
  | CHANGE SECTION RULE
  |--------------------------------------------------------------------------
  */

  const changeSectionRule = (category, sectionIndex, ruleIndex, value) => {
    setRulesData((prev) => {
      const sections = [...(prev[category]?.sections || [])];

      const section = {
        ...sections[sectionIndex],
      };

      const rules = [...(section.rules || [])];

      rules[ruleIndex] = value;

      section.rules = rules;

      sections[sectionIndex] = section;

      return {
        ...prev,

        [category]: {
          ...prev[category],
          sections,
        },
      };
    });
  };

  /*
  |--------------------------------------------------------------------------
  | ADD SECTION RULE
  |--------------------------------------------------------------------------
  */

  const addSectionRule = (category, sectionIndex) => {
    setRulesData((prev) => {
      const sections = [...(prev[category]?.sections || [])];

      sections[sectionIndex] = {
        ...sections[sectionIndex],

        rules: [...(sections[sectionIndex]?.rules || []), ""],
      };

      return {
        ...prev,

        [category]: {
          ...prev[category],
          sections,
        },
      };
    });
  };

  /*
  |--------------------------------------------------------------------------
  | DELETE SECTION RULE
  |--------------------------------------------------------------------------
  */

  const deleteSectionRule = (category, sectionIndex, ruleIndex) => {
    if (!window.confirm("Are you sure you want to delete this rule?")) {
      return;
    }

    setRulesData((prev) => {
      const sections = [...(prev[category]?.sections || [])];

      sections[sectionIndex] = {
        ...sections[sectionIndex],

        rules: (sections[sectionIndex]?.rules || []).filter(
          (_, index) => index !== ruleIndex,
        ),
      };

      return {
        ...prev,

        [category]: {
          ...prev[category],
          sections,
        },
      };
    });
  };

  /*
  |--------------------------------------------------------------------------
  | ADD SECTION
  |--------------------------------------------------------------------------
  */

  const addSection = (category) => {
    setRulesData((prev) => ({
      ...prev,

      [category]: {
        ...prev[category],

        sections: [
          ...(prev[category]?.sections || []),

          {
            heading: "New Section",
            rules: [""],
          },
        ],
      },
    }));
  };

  /*
  |--------------------------------------------------------------------------
  | DELETE SECTION
  |--------------------------------------------------------------------------
  */

  const deleteSection = (category, sectionIndex) => {
    if (
      !window.confirm("Are you sure you want to delete this complete section?")
    ) {
      return;
    }

    setRulesData((prev) => ({
      ...prev,

      [category]: {
        ...prev[category],

        sections: (prev[category]?.sections || []).filter(
          (_, index) => index !== sectionIndex,
        ),
      },
    }));
  };

  /*
  |--------------------------------------------------------------------------
  | CANCEL EDIT
  |--------------------------------------------------------------------------
  */

  const cancelEdit = async () => {
    await fetchRules();
    setEditing(false);
  };

  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="rules-loading">
        <div className="rules-spinner"></div>
        <p>Loading Rules...</p>
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | UI
  |--------------------------------------------------------------------------
  */

  return (
    <div className="rules-page">
      {/* =====================================================
          MAIN CATEGORY LIST
      ===================================================== */}

      {!selectedCategory && (
        <>
          <div className="rules-header">
            <div>
              <h2>Rules & Conditions</h2>

              {/* <p>Select a category to view and manage its rules.</p> */}
            </div>
          </div>

          <div className="rules-main-list">
            {Object.entries(rulesData).map(([category, categoryData]) => (
              <button
                type="button"
                className="rules-main-item"
                key={category}
                onClick={() => {
                  setSelectedCategory(category);
                  setEditing(false);
                }}
              >
                <div className="rules-main-item-content">
                  {/* <div className="rules-main-icon">
                    {categoryData.title
                      ? categoryData.title.charAt(0).toUpperCase()
                      : "R"}
                  </div> */}

                  <div className="rules-main-text">
                    <h3>{categoryData.title}</h3>

                    <span>
                      {Array.isArray(categoryData.sections)
                        ? `${categoryData.sections.length} sections`
                        : `${categoryData.rules?.length || 0} rules`}
                    </span>
                  </div>
                </div>

                <FaChevronRight className="rules-main-arrow" />
              </button>
            ))}
          </div>
        </>
      )}

      {/* =====================================================
          SELECTED CATEGORY
      ===================================================== */}

      {selectedCategory && rulesData[selectedCategory] && (
        <div className="rules-detail-page">
          {}

          <div className="rules-detail-top">
            <button
              type="button"
              className="rules-back-btn"
              onClick={() => {
                if (editing) {
                  const confirmLeave = window.confirm(
                    "Your unsaved changes will be lost. Do you want to go back?",
                  );

                  if (!confirmLeave) {
                    return;
                  }

                  fetchRules();
                }

                setEditing(false);
                setSelectedCategory(null);
              }}
            >
              <FaArrowLeft />
              Back
            </button>
          </div>

          {/* =================================================
                CATEGORY HEADER
            ================================================= */}

          <div className="rules-detail-header">
            <div className="rules-detail-title">
              {editing ? (
                <input
                  type="text"
                  className="rules-title-input"
                  value={rulesData[selectedCategory].title || ""}
                  onChange={(e) =>
                    changeTitle(selectedCategory, e.target.value)
                  }
                />
              ) : (
                <>
                  <h2>{rulesData[selectedCategory].title}</h2>

                  {/* <p>View and manage rules for this category.</p> */}
                </>
              )}
            </div>

            {!editing && (
              <button
                type="button"
                className="rules-edit-btn"
                onClick={() => setEditing(true)}
              >
                <FaEdit />
                Edit
              </button>
            )}
          </div>

          {/* =================================================
                SIMPLE RULES
            ================================================= */}

          {Array.isArray(rulesData[selectedCategory].rules) && (
            <div className="rules-detail-card">
              <div className="rules-detail-card-header">
                <h3>Rules</h3>

                <span>{rulesData[selectedCategory].rules.length} Rules</span>
              </div>

              <div className="rules-list">
                {rulesData[selectedCategory].rules.length === 0 && !editing && (
                  <div className="rules-empty">
                    No rules have been added yet.
                  </div>
                )}

                {rulesData[selectedCategory].rules.map((rule, ruleIndex) => (
                  <div className="rule-row" key={ruleIndex}>
                    <span className="rule-number">{ruleIndex + 1}</span>

                    {editing ? (
                      <>
                        <input
                          type="text"
                          className="rule-input"
                          value={rule}
                          placeholder="Enter rule..."
                          onChange={(e) =>
                            changeSimpleRule(
                              selectedCategory,
                              ruleIndex,
                              e.target.value,
                            )
                          }
                        />

                        <button
                          type="button"
                          className="rule-delete-btn"
                          title="Delete Rule"
                          onClick={() =>
                            deleteSimpleRule(selectedCategory, ruleIndex)
                          }
                        >
                          <FaTrash />
                        </button>
                      </>
                    ) : (
                      <p>{rule}</p>
                    )}
                  </div>
                ))}

                {editing && (
                  <button
                    type="button"
                    className="rules-add-btn"
                    onClick={() => addSimpleRule(selectedCategory)}
                  >
                    <FaPlus />
                    Add Rule
                  </button>
                )}
              </div>
            </div>
          )}

          {/* =================================================
                SECTION BASED RULES
            ================================================= */}

          {Array.isArray(rulesData[selectedCategory].sections) && (
            <div className="rules-detail-sections">
              {rulesData[selectedCategory].sections.map(
                (section, sectionIndex) => (
                  <div className="rules-detail-card" key={sectionIndex}>
                    <div className="rules-detail-card-header">
                      <div className="rules-section-title">
                        {editing ? (
                          <input
                            type="text"
                            className="rules-section-input"
                            value={section.heading || ""}
                            onChange={(e) =>
                              changeSectionHeading(
                                selectedCategory,
                                sectionIndex,
                                e.target.value,
                              )
                            }
                          />
                        ) : (
                          <h3>{section.heading}</h3>
                        )}
                      </div>

                      <div className="rules-section-actions">
                        {!editing && (
                          <span>{section.rules?.length || 0} Rules</span>
                        )}

                        {editing && (
                          <button
                            type="button"
                            className="section-delete-btn"
                            onClick={() =>
                              deleteSection(selectedCategory, sectionIndex)
                            }
                          >
                            <FaTrash />
                            Delete Section
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="rules-list">
                      {(!section.rules || section.rules.length === 0) &&
                        !editing && (
                          <div className="rules-empty">
                            No rules have been added yet.
                          </div>
                        )}

                      {section.rules?.map((rule, ruleIndex) => (
                        <div className="rule-row" key={ruleIndex}>
                          <span className="rule-number">{ruleIndex + 1}</span>

                          {editing ? (
                            <>
                              <input
                                type="text"
                                className="rule-input"
                                value={rule}
                                placeholder="Enter rule..."
                                onChange={(e) =>
                                  changeSectionRule(
                                    selectedCategory,
                                    sectionIndex,
                                    ruleIndex,
                                    e.target.value,
                                  )
                                }
                              />

                              <button
                                type="button"
                                className="rule-delete-btn"
                                title="Delete Rule"
                                onClick={() =>
                                  deleteSectionRule(
                                    selectedCategory,
                                    sectionIndex,
                                    ruleIndex,
                                  )
                                }
                              >
                                <FaTrash />
                              </button>
                            </>
                          ) : (
                            <p>{rule}</p>
                          )}
                        </div>
                      ))}

                      {editing && (
                        <button
                          type="button"
                          className="rules-add-btn"
                          onClick={() =>
                            addSectionRule(selectedCategory, sectionIndex)
                          }
                        >
                          <FaPlus />
                          Add Rule
                        </button>
                      )}
                    </div>
                  </div>
                ),
              )}

              {editing && (
                <button
                  type="button"
                  className="rules-add-section-btn"
                  onClick={() => addSection(selectedCategory)}
                >
                  <FaPlus />
                  Add New Section
                </button>
              )}
            </div>
          )}

          {/* =================================================
                BOTTOM ACTIONS
            ================================================= */}

          <div className="rules-bottom-actions">
            {!editing ? (
              <button
                type="button"
                className="rules-edit-btn rules-bottom-edit"
                onClick={() => setEditing(true)}
              >
                <FaEdit />
                Edit
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="rules-cancel-btn"
                  disabled={saving}
                  onClick={cancelEdit}
                >
                  <FaTimes />
                  Cancel
                </button>

                <button
                  type="button"
                  className="rules-save-btn"
                  disabled={saving}
                  onClick={saveRules}
                >
                  <FaSave />

                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
